import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { LabelComponent } from "@components/label/label.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TableComponent as TableComponent_1 , TableComponent } from "@components/table/table.component";
import { BehaviorSubject, Subject } from "rxjs";
import { IMessageLabel, IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { RaskColumn } from "~app/common/components/table/models/rask-column";
import { RaskDataChanged } from "~app/common/components/table/models/rask-data-changed";

import { FormBinderService } from "~app/common/services/form-binder.service";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { ProduktdefinitionViewModel } from "../../models/ProduktdefinitionViewModel";
import { ProduktinstruktionService } from "../../services/produktinstruktion.service";

@Component({
    selector: "rask-produktinstruktioner-massedit-header",
    templateUrl: "./produktinstruktioner-massedit-header.component.html",
    styleUrls: ["./produktinstruktioner-massedit-header.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        FormsModule,
        ReactiveFormsModule,
        SelectorComponent,
        LabelComponent,
        TableComponent_1,
        InputComponent,
        CheckboxComponent,
        InputNumberComponent,
        MessagePanelComponent
    ]
})
export class ProduktinstruktionerMasseditHeaderComponent implements OnInit, OnChanges {
    dataChanged = new Subject<RaskDataChanged>();
    form: UntypedFormGroup;
    @Input() grundProduktinstruktionViewModel: ProduktdefinitionViewModel;
    messageLabels: IMessageLabel[] = [
        { severity: MessageSeverity.Warning,
            labelText: "Observera" }
    ];
    @Input() rootForm: UntypedFormGroup;
    @Output() selectedProduktinstruktionChange = new EventEmitter();
    tableColumns: RaskColumn[];
    @ViewChild(TableComponent) tableComp: TableComponent;
    @Input() valdaProduktinstruktioner: ProduktdefinitionViewModel[];
    @Input() validationContext: IValidationContext;
    validationMessages$ = new BehaviorSubject<IMessageModel[]>([]);
    versionsNummerForAllaPuid = false;

    private readonly formBinder = inject(FormBinderService);
    private readonly formId = "produktinstruktioner-massedit-header";
    private readonly produktinstruktionService = inject(ProduktinstruktionService);

    get hasAlerts() {
        return this.produktinstruktionService.alerts$.value.length > 0;
    }

    canChangeProduktintruktion() {
        return !this.rootForm.dirty;
    }

    canEditVersion() {
        return !this.versionsNummerForAllaPuid;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.viewModel && changes.viewModel.currentValue !== changes.viewModel.previousValue) {
            this.formBinder.reBindForm(this.form, this.grundProduktinstruktionViewModel.headerData);
        }
    }

    ngOnInit(): void {
        this.form = this.formBinder.CreateGroup(this.grundProduktinstruktionViewModel.headerData);
        this.rootForm.addControl(this.formId, this.form);
        this.form.get("version")?.disable();
        this.dataChanged.subscribe(x => {
            if (this.grundProduktinstruktionViewModel.headerData.puid === x.model.headerData.puid) {
                this.grundProduktinstruktionViewModel.headerData.version = x.model.headerData.version;
            }
            this.form.markAsDirty();
        });
        this.setupColumns(this.dataChanged);
        this.produktinstruktionService.alerts$.subscribe(x => {
            this.validationMessages$.next(x);
        });
    }

    produktinstruktionChanged() {
        this.selectedProduktinstruktionChange.emit(this.grundProduktinstruktionViewModel);
        this.formBinder.reBindForm(this.form, this.grundProduktinstruktionViewModel.headerData);
    }

    produktinstruktionViewFormatter = (result: ProduktdefinitionViewModel) => result ? `${result.headerData.namn} (${result.headerData.puid})` : null;

    versionsnummerChanged() {
        this.valdaProduktinstruktioner.forEach(element => {
            element.headerData.version = this.grundProduktinstruktionViewModel.headerData.version;
        });
        this.tableComp.refreshGrid();
    }

    versionsnummerCheckboxChanged($event: boolean) {
        if ($event) {
            this.form.get("version")?.enable();
        } else {
            this.form.get("version")?.disable();
        }
        this.setupColumns(this.dataChanged);
        setTimeout(() => {
            this.tableComp.updateColdefs();
        }, 50);
    }

    private setupColumns(dataChanged: Subject<RaskDataChanged>) {
        this.tableColumns = [
            {
                editable: false,
                headerName: "Namn",
                field: "headerData.namn",
                filter: false,
                type: "sref",
                componentOptions: (row) =>
                    ({
                        srefParams: {},
                        sref: `/produktinstruktioner/${row.headerData.puid}`
                    })
            },
            {
                readonly: true,
                headerName: "Puid",
                field: "headerData.puid",
                filter: false,
                sortable: false,
                editable: false,
                maxWidth: 160,
                type: "span"
            },
            {
                headerName: "Version",
                field: "version",
                filter: false,
                sortable: false,
                editable: !this.versionsNummerForAllaPuid,
                dataChanged: dataChanged,
                maxWidth: 90
            }
        ] as any[] as RaskColumn[];
    }
}
